/**
 * The email address of the person who signed in last, and the only reason it is stored.
 *
 * Signing in here is usually one click and nothing else. The MIKE Cloud session outlives
 * ours and the platform gives us no way to make it ask who you are: measured on
 * 2026-08-26, `/authorize` accepts `prompt=login`, `prompt=select_account` and
 * `login_hint`, and forwards none of them to Entra, where the credential check actually
 * happens. So a second person at the same browser would be signed in as the first, with
 * nothing on screen to give it away.
 *
 * Remembering the address is what makes that visible: the sign-in screen can say who the
 * one-click path signs in as, and offer a way out of it. It is written to `localStorage`
 * rather than kept in memory because the silent path survives a browser restart, which is
 * exactly the case where nobody remembers who used this machine last. An email address is
 * not a credential, and anyone who can read it here could equally read it by clicking
 * sign in, so this stores no more than the browser already gives away.
 *
 * Cleared by every deliberate sign-out, since from then on the next sign-in has to name
 * an account anyway. On the Entra path that is also what asks Microsoft for its account
 * picker: with nobody remembered, `authorizeExtras` sends `prompt=select_account`.
 */

const KEY = 'mikeai.last-account';

/** `localStorage`, or null where it does not exist or the browser refuses it. */
function defaultStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // Private modes and blocked-storage settings throw on access rather than returning
    // null, and a sign-in screen is not worth crashing over.
    return null;
  }
}

export function readAccount(store: Storage | null = defaultStore()): string | null {
  try {
    return store?.getItem(KEY) || null;
  } catch {
    return null;
  }
}

export function rememberAccount(
  email: string | null,
  store: Storage | null = defaultStore(),
): void {
  if (!email) return;
  try {
    store?.setItem(KEY, email);
  } catch {
    // Full or read-only storage. The screen simply falls back to not naming an account.
  }
}

export function forgetAccount(store: Storage | null = defaultStore()): void {
  try {
    store?.removeItem(KEY);
  } catch {
    // As above.
  }
}
